import { useQuery } from '@tanstack/react-query';
import { SCHEDULE_SUB_SLUGS } from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { Callout } from '../../components/Callout.js';
import { QueryState } from '../../components/QueryState.js';

type SchedSub = keyof typeof SCHEDULE_SUB_SLUGS;

const SUB_BY_SLUG = Object.fromEntries(
  Object.entries(SCHEDULE_SUB_SLUGS).map(([k, v]) => [v, k as SchedSub]),
) as Record<string, SchedSub>;

/** Standard DOOD notation. SW start work · W work · WF work finish · H hold. */
const DOOD_STYLE: Record<string, [string, string]> = {
  SW: ['#D1FADF', '#067647'],
  W: ['#EAECF0', '#344054'],
  WF: ['#FEE4E2', '#B42318'],
  H: ['#FDF2CC', '#B54708'],
};

interface Day {
  id: string;
  sequence: number;
  shootDate: string | null;
  label: string | null;
  assignments: { id: string; sceneId: string; candidateId: string }[];
}

export function ScheduleTab({
  projectId,
  subSlug,
  onNavigate,
}: {
  projectId: string;
  subSlug: string | undefined;
  onNavigate: (path: string) => void;
}) {
  const sub: SchedSub = (subSlug ? SUB_BY_SLUG[subSlug] : undefined) ?? 'CALENDAR';
  const setSub = (next: SchedSub) => {
    onNavigate(`/projects/${projectId}/schedule/${SCHEDULE_SUB_SLUGS[next]}`);
  };

  return (
    <div data-testid="schedule-tab">
      {/* Wraps rather than overflowing: four buttons plus the caption are wider
          than a phone, and a row that cannot wrap drags the whole page
          sideways. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {(['CALENDAR', 'STRIPBOARD', 'DOOD', 'RED_FLAGS'] as const).map((s) => (
          <button
            key={s}
            type="button"
            className="sub"
            data-on={sub === s ? '1' : '0'}
            data-testid={`sched-${s}`}
            onClick={() => {
              setSub(s);
            }}
          >
            {s === 'RED_FLAGS' ? 'Red flags' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
        <span className="mm" style={{ fontSize: 11.5, alignSelf: 'center', marginLeft: 12 }}>
          One schedule — these are views of the same data.
        </span>
      </div>

      <div style={{ border: '1px solid var(--color-divider)', padding: 24, background: 'var(--color-bg)' }}>
        {sub === 'CALENDAR' ? <CalendarView projectId={projectId} /> : null}
        {sub === 'STRIPBOARD' ? <StripboardView projectId={projectId} /> : null}
        {sub === 'DOOD' ? <DoodView projectId={projectId} /> : null}
        {sub === 'RED_FLAGS' ? <RedFlagsView projectId={projectId} /> : null}
      </div>
    </div>
  );
}

function StripboardView({ projectId }: { projectId: string }) {
  const strips = useQuery({
    queryKey: ['strips', projectId],
    queryFn: () => api.strips(projectId),
  });

  return (
    <div data-testid="stripboard-view">
      <p className="dh" style={{ fontSize: 20 }}>
        Stripboard
      </p>
      <p className="mm" style={{ fontSize: 12.5, margin: '8px 0 16px', lineHeight: 1.6, maxWidth: '68ch' }}>
        One strip per scene. Re-ordering the board changes the schedule itself, so a sort is
        previewed and applied deliberately rather than taking effect as you look at it.
      </p>

      <QueryState query={strips} subject="the stripboard" testId="strips" />

      {strips.data && strips.data.length === 0 ? (
        <p className="mm" style={{ fontSize: 13 }} data-testid="strips-empty">
          No scenes parsed yet.
        </p>
      ) : null}

      {/* A strip board is legitimately wider than a phone; a horizontally
          scrolling PAGE is not. The min-width keeps the columns readable
          instead of crushing them, and the scroller keeps that off the page. */}
      <div className="scrollx">
      <div style={{ display: 'grid', gap: 1, background: 'var(--color-divider)', border: '1px solid var(--color-divider)', minWidth: 620 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '56px 54px minmax(0,1.6fr) 72px 64px 90px',
            gap: 12,
            padding: '10px 12px',
            background: 'var(--color-surface)',
          }}
        >
          <span className="colh">Sc</span>
          <span className="colh">I/E</span>
          <span className="colh">Set</span>
          <span className="colh">D/N</span>
          <span className="colh">Pages</span>
          <span className="colh">Day</span>
        </div>
        {strips.data?.map((s) => (
          <div
            key={s.sceneId}
            data-testid={`strip-${s.sceneId}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '56px 54px minmax(0,1.6fr) 72px 64px 90px',
              gap: 12,
              padding: '10px 12px',
              background: 'var(--color-bg)',
              alignItems: 'center',
              // Night and exterior read at a glance on a physical board; the
              // edge colour carries that here without being the only signal.
              borderLeft: `4px solid ${
                s.timeOfDay === 'NIGHT' || s.timeOfDay === 'DUSK'
                  ? 'var(--ink)'
                  : s.intExt === 'EXT'
                    ? 'var(--color-neutral-400)'
                    : 'var(--color-divider)'
              }`,
            }}
          >
            <span className="mm" style={{ fontSize: 12, fontWeight: 700 }}>
              {s.sceneNumber ?? '—'}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600 }}>{s.intExt ?? '—'}</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {s.slugline}
            </span>
            <span style={{ fontSize: 12 }}>{s.timeOfDay ?? '—'}</span>
            <span className="mm" style={{ fontSize: 12 }}>
              {/* Eighths, the production unit — 2 3/8 is exact where 2.375 is
                  a decimal nobody schedules in. */}
              {s.pageEighths ? `${String(Math.floor(s.pageEighths / 8))} ${String(s.pageEighths % 8)}/8` : '—'}
            </span>
            <span className="mm" style={{ fontSize: 12 }}>
              {s.daySequence ? `Day ${String(s.daySequence)}` : 'unscheduled'}
            </span>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

function DoodView({ projectId }: { projectId: string }) {
  const dood = useQuery({ queryKey: ['dood', projectId], queryFn: () => api.dood(projectId) });

  return (
    <div data-testid="dood-view">
      <p className="dh" style={{ fontSize: 20 }}>
        Day out of days
      </p>
      <p className="mm" style={{ fontSize: 12.5, margin: '8px 0 16px', lineHeight: 1.6, maxWidth: '68ch' }}>
        SW start work · W work · WF work finish · <strong>H hold</strong>. A hold is a day a
        performer is not called but is still committed — and still paid, which is why it is never
        left blank.
      </p>

      {dood.data && dood.data.rows.length === 0 ? (
        <p className="mm" style={{ fontSize: 13 }} data-testid="dood-empty">
          No cast confirmed yet. Cast comes from the breakdown once a person has confirmed it — a
          schedule people are paid against is not built on unreviewed extraction.
        </p>
      ) : null}

      {dood.data && dood.data.rows.length > 0 ? (
        // One column per shoot day, so this outgrows any viewport on a real
        // schedule — it scrolls itself rather than the page.
        <div className="scrollx">
        <table data-testid="dood-table" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th className="colh" style={{ textAlign: 'left', padding: '8px 12px' }}>
                Cast
              </th>
              {dood.data.days.map((d) => (
                <th key={d} className="colh" style={{ padding: '8px 10px' }}>
                  {d}
                </th>
              ))}
              <th className="colh" style={{ padding: '8px 12px' }}>
                Days
              </th>
            </tr>
          </thead>
          <tbody>
            {dood.data.rows.map((row) => (
              <tr key={row.castName} data-testid={`dood-${row.castName}`}>
                <th scope="row" style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12.5, fontWeight: 600 }}>
                  {row.castName}
                </th>
                {row.cells.map((cell, i) => {
                  const [bg, fg] = DOOD_STYLE[cell] ?? ['transparent', 'var(--color-neutral-600)'];
                  return (
                    <td key={i} style={{ padding: 4, textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          width: 26,
                          height: 22,
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 700,
                          background: bg,
                          color: fg,
                        }}
                      >
                        {cell}
                      </span>
                    </td>
                  );
                })}
                <td style={{ padding: '8px 12px', fontSize: 12, fontWeight: 700 }}>{row.workDays}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : null}
    </div>
  );
}

function RedFlagsView({ projectId }: { projectId: string }) {
  const warnings = useQuery({
    queryKey: ['schedule-warnings', projectId],
    queryFn: () => api.scheduleWarnings(projectId),
  });

  return (
    <div data-testid="red-flags-view" style={{ display: 'grid', gap: 12 }}>
      <p className="dh" style={{ fontSize: 20 }}>
        Red flags
      </p>
      {warnings.data && warnings.data.length === 0 ? (
        <p className="mm" style={{ fontSize: 13 }} data-testid="flags-empty">
          Nothing flagged.
        </p>
      ) : null}
      {warnings.data?.map((w) => (
        <Callout
          key={w.assignmentId}
          tone="warn"
          title="This day uses the previous primary location"
          testId={`stale-primary-${w.assignmentId}`}
        >
          {w.message} Nothing has been changed — updating it is your call.
        </Callout>
      ))}
    </div>
  );
}

function CalendarView({ projectId }: { projectId: string }) {
  const board = useQuery({
    queryKey: ['schedule', projectId],
    queryFn: () => api.scheduleBoard(projectId) as Promise<Day[]>,
  });

  const warnings = useQuery({
    queryKey: ['schedule-warnings', projectId],
    queryFn: () => api.scheduleWarnings(projectId),
  });

  return (
    <div data-testid="calendar-view" style={{ display: 'grid', gap: 20 }}>
      {warnings.data?.map((w) => (
        <Callout
          key={w.assignmentId}
          tone="warn"
          title="This day uses the previous primary location"
          testId={`cal-stale-${w.assignmentId}`}
        >
          {/* The product refuses to move this on its own, and says so. The
              producer is the only party who knows whether crew, permits and
              cast can move with it. */}
          {w.message} Nothing has been changed — updating it is your call.
        </Callout>
      ))}

      {board.data && board.data.length === 0 ? (
        <p className="mm" style={{ fontSize: 13 }} data-testid="schedule-empty">
          No shooting days yet. A scene can be scheduled once its requirement has a confirmed
          primary location.
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: 10 }}>
        {board.data?.map((day) => (
          <div key={day.id} className="card1" style={{ padding: '16px 18px' }} data-testid={`day-${day.id}`}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span className="dh" style={{ fontSize: 19 }}>
                Day {day.sequence}
              </span>
              {/* A schedule is an ORDER before it is a calendar; an undated day
                  is a normal state rather than an omission. */}
              <span className="mm" style={{ fontSize: 12.5 }}>
                {day.shootDate ?? 'date to be confirmed'}
              </span>
              {day.label ? <span className="chip">{day.label}</span> : null}
              <span className="mm" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
                {day.assignments.length} scene{day.assignments.length === 1 ? '' : 's'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
